import { Controller } from '@hotwired/stimulus'

// Generic selection controller for checkbox lists.
// Dispatches "selection:change" with { ids } when selection changes.
export default class extends Controller {
  static targets = ['item', 'selectAll']

  // Lifecycle: handle dynamically added/removed checkboxes (e.g., Turbo Streams)
  itemTargetConnected(checkbox) {
    checkbox.addEventListener('change', this.#onItemChange)
    this.#refresh()
  }

  itemTargetDisconnected(checkbox) {
    checkbox.removeEventListener('change', this.#onItemChange)
    this.#refresh()
  }

  selectAllTargetConnected(checkbox) {
    checkbox.addEventListener('change', this.#onSelectAllChange)
    this.#refresh()
  }

  selectAllTargetDisconnected(checkbox) {
    checkbox.removeEventListener('change', this.#onSelectAllChange)
  }

  // Bound event handlers
  #onItemChange = () => {
    this.#update()
  }

  #onSelectAllChange = (event) => {
    const checked = event.target.checked

    this.itemTargets.forEach((checkbox) => {
      checkbox.checked = checked
      this.#triggerInputEvent(checkbox)
    })

    this.#update()
  }

  reset() {
    this.itemTargets.forEach((cb) => (cb.checked = false))
    this.#update()
  }

  // Private

  #refresh() {
    this.#updateSelectAllCheckbox()
  }

  #update() {
    this.#refresh()
    this.#dispatchChange()
  }

  #updateSelectAllCheckbox() {
    if (!this.hasSelectAllTarget) return

    const checkbox = this.selectAllTarget
    const allSelected = this.#allSelected()
    const someSelected = this.#someSelected()

    checkbox.checked = allSelected
    checkbox.indeterminate = someSelected && !allSelected
  }

  #dispatchChange() {
    this.dispatch('change', {
      detail: { ids: this.#selectedIds },
      target: window,
    })
  }

  #triggerInputEvent(checkbox) {
    checkbox.dispatchEvent(
      new Event('input', { bubbles: false, cancelable: true }),
    )
  }

  #allSelected() {
    return (
      this.itemTargets.length > 0 && this.itemTargets.every((cb) => cb.checked)
    )
  }

  #someSelected() {
    return this.itemTargets.some((cb) => cb.checked)
  }

  // Derive selected IDs from DOM - single source of truth
  get #selectedIds() {
    return this.itemTargets.filter((cb) => cb.checked).map((cb) => cb.value)
  }
}
